import type { Image, Link, RichText } from '@eneco/cms-types';
import { asRichText } from '@eneco/cms-types';

import type { JssField, JssImageFieldValue, JssLinkFieldValue } from './jss-types';

const isObject = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null;

/**
 * What a JSS field may carry beside `value`.
 *
 * `value` alone is not enough to identify a field, because plenty of authored
 * content has a `value` property of its own. A droplist's options are
 * `{ name, value, label }` and a flow's navigation steps are the same, so
 * matching on `value` collapsed each option to its own value string and threw
 * the rest away: a navigation rendered four unlabelled steps and a radio group
 * rendered choices with no text, both silently.
 *
 * The distinction is that a field's other keys are metadata about the field
 * while an option's are content. `metadata` rides along in the Experience
 * Editor and `linktype` sits on the route-level path fields.
 *
 * Editor markup needs three names, because JSS does not have one name for it:
 * text, image and date fields carry `editable`, while a link carries
 * `editableFirstPart` and `editableLastPart`, its markup wrapping the link's
 * children. Listing only `editable` meant every link failed this test in the
 * Experience Editor and was left wrapped, so components read `href` off the
 * wrapper and found nothing. Outside the editor those keys are absent, which
 * is why it rendered correctly everywhere else.
 *
 * This is the whole set that `@sitecore-jss` 22.12.3 declares. A JSS upgrade
 * that adds a fourth name would leave that field wrapped, and silently, so
 * check this list when the dependency moves.
 *
 * A shape carrying any other key is content.
 */
const FIELD_KEYS = new Set(['value', 'editable', 'editableFirstPart', 'editableLastPart', 'metadata', 'linktype']);

export const isField = (value: unknown): value is JssField =>
  isObject(value) && 'value' in value && Object.keys(value).every(key => FIELD_KEYS.has(key));

/**
 * An item referenced by a multilist, droplink or droptree. JSS serialises all
 * three the same way, as `{ id?, url?, name?, displayName?, fields: { ... } }`:
 * the item's own content lives one level down, under `fields`. A multilist
 * holds many such references and a droplink one, but the shape is identical,
 * and the referenced item need not sit under the datasource.
 *
 * That key is structure rather than a field wrapper, so `isField` does not match
 * it and the generic object branch would keep it. Sanity has no equivalent, so
 * leaving it would force every component with a list either to unwrap it by hand
 * or to declare the wrapper in its content type, which puts a Sitecore transport
 * shape in the layer that exists to be free of one.
 */
export const isItem = (value: unknown): value is Record<string, unknown> & { fields: Record<string, unknown> } =>
  isObject(value) && 'fields' in value && !('value' in value) && isObject(value.fields);

/**
 * Recursively unwrap `{ value: ... }` JSS field shapes into plain values.
 * This is the core trick that lets components write `content.title`
 * instead of `f('title.value')`.
 */
export const unwrapFields = (fields: Record<string, unknown> | undefined): Record<string, unknown> => {
  if (!fields) return {};
  const out: Record<string, unknown> = {};
  for (const [key, raw] of Object.entries(fields)) {
    out[key] = unwrap(raw);
  }
  return out;
};

const unwrap = (raw: unknown): unknown => {
  if (Array.isArray(raw)) return raw.map(unwrap);
  if (isField(raw)) {
    // Recurse so nested object/array shapes inside `value` are also unwrapped.
    return unwrap(raw.value);
  }
  if (isItem(raw)) {
    // Lift the item's own content up a level. The remaining keys (`id`, `name`,
    // `url`, `displayName`) are kept rather than dropped, because components use
    // them for stable list keys. Fields win a name clash: an authored field is
    // content, the siblings are item metadata.
    const { fields, ...rest } = raw;
    return unwrapFields({ ...rest, ...fields });
  }
  if (isObject(raw)) {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(raw)) out[k] = unwrap(v);
    return out;
  }
  return raw;
};

interface JssFormat {
  format: string;
  src: string;
  width?: string;
  height?: string;
}

const breakpointForFormat = (format: string): string | undefined => {
  switch (format) {
    case 'small': return 'sm';
    case 'medium': return 'md';
    case 'large': return 'lg';
    case 'extraLarge': return 'xl';
    case 'widescreen': return 'xxl';
    default: return undefined;
  }
};

const stripPx = (value: string | undefined): number | undefined => {
  if (!value) return undefined;
  const parsed = Number.parseInt(value.replace('px', ''), 10);
  return Number.isFinite(parsed) ? parsed : undefined;
};

const fromFormats = (alt: string | undefined, formats: JssFormat[]): Image | undefined => {
  const valid = formats.filter(f => typeof f?.src === 'string' && f.src.length > 0);
  if (valid.length === 0) return undefined;
  const primary = valid.find(f => f.format === 'small') ?? valid.find(f => f.format === 'default') ?? valid[0];
  const sources: Record<string, string> = {};
  for (const f of valid) {
    const bp = breakpointForFormat(f.format);
    if (bp) sources[bp] = f.src;
  }
  const srcSet = valid
    .map(f => (f.width ? `${f.src} ${f.width.replace('px', 'w')}` : f.src))
    .join(', \n');
  const width = stripPx(primary.width);
  const height = stripPx(primary.height);
  const aspectRatio = width && height ? `${width}/${height}` : undefined;
  return {
    src: primary.src,
    ...(alt !== undefined ? { alt } : {}),
    ...(width !== undefined ? { width } : {}),
    ...(height !== undefined ? { height } : {}),
    ...(Object.keys(sources).length > 0 ? { sources } : {}),
    ...(srcSet ? { srcSet } : {}),
    ...(aspectRatio ? { aspectRatio } : {}),
  };
};

export const toImage = (raw: unknown): Image | undefined => {
  if (raw === null || raw === undefined) return undefined;
  const value = isField(raw) ? raw.value : raw;
  if (!isObject(value)) return undefined;
  // FocalPointImage shape: { alt, formats: [{format, src, width, height}, ...] }
  if (Array.isArray((value as { formats?: unknown }).formats)) {
    const formats = (value as { formats: JssFormat[] }).formats;
    const alt = typeof (value as { alt?: unknown }).alt === 'string' ? (value as { alt: string }).alt : undefined;
    return fromFormats(alt, formats);
  }
  const img = value as JssImageFieldValue;
  if (typeof img.src !== 'string' || img.src.length === 0) return undefined;
  const width = typeof img.width === 'string' ? Number(img.width) || undefined : img.width;
  const height = typeof img.height === 'string' ? Number(img.height) || undefined : img.height;
  return {
    src: img.src,
    ...(img.alt !== undefined ? { alt: img.alt } : {}),
    ...(width !== undefined ? { width } : {}),
    ...(height !== undefined ? { height } : {}),
  };
};

export const toLink = (raw: unknown): Link | undefined => {
  if (raw === null || raw === undefined) return undefined;
  const value = isField(raw) ? raw.value : raw;
  if (!isObject(value)) return undefined;
  const link = value as JssLinkFieldValue;
  if (typeof link.href !== 'string' || link.href.length === 0) return undefined;
  return {
    href: link.href,
    ...(link.text !== undefined ? { text: link.text } : {}),
    ...(link.target !== undefined ? { target: link.target } : {}),
    ...(link.linktype !== undefined ? { linkType: link.linktype } : {}),
  };
};

/**
 * Sitecore stores rich text as raw HTML strings. We keep the markup as-is
 * (consumers are responsible for safe rendering) and brand it as `RichText`.
 */
export const toRichText = (raw: unknown): RichText | undefined => {
  if (raw === null || raw === undefined) return undefined;
  const value = isField(raw) ? raw.value : raw;
  if (typeof value !== 'string' || value.length === 0) return undefined;
  return asRichText(value);
};
